import type { DocsPageTranslation } from './types'

// Initial LLM-assisted Korean docs translation. A Korean filmmaker reviewer
// should look over this copy before release; source_en_hash marks the English
// source revision used here.
export const DOCS_PAGE_TRANSLATIONS_KO: Record<string, DocsPageTranslation> = {
  'getting-started/welcome': {
    source_en_hash: 'v1:1ehqbgkba04',
    title: '환영합니다',
    description: 'Martini가 무엇이며 영상 제작자가 어떻게 사용하는지 안내합니다.',
    overview: `Martini는 AI 영상을 위한 필름 세트입니다. 무한히 펼쳐진 캔버스 위에서 샷을 배치하고, AI 모델로 생성하고, 의도한 결과가 나올 때까지 반복하고, 마지막에 모든 것을 타임라인에서 편집합니다. 빠른 반복, 공간적 정리, 실시간 협업을 위해 만들어졌습니다.

![](/docs-imgs/welcome1.mp4)

||V||를 눌러 샷을 배치합니다. 프롬프트를 작성하고, 모델을 선택하고, 생성합니다. 테이크가 맞지 않으면 프롬프트를 조정하거나 레퍼런스 이미지를 바꾸거나 다른 모델을 시도합니다. 새 테이크는 원래 테이크 옆에 남아 있어 비교할 수 있습니다.

![](/docs-imgs/welcome2.mp4)

필요한 샷이 모두 모이면 타임라인으로 끌어다 놓고 익스포트합니다. Martini는 Veo, Kling, Sora 등 여러 모델을 하나의 화면에서 다루기 때문에, 각 샷에 어울리는 모델을 고를 수 있습니다.`,
  },
  'getting-started/first-shot': {
    source_en_hash: 'v1:1516v8f2dr5',
    title: '첫 번째 샷',
    description: '이미지를 생성하고 그것을 영상으로 만듭니다.',
    overview: `모든 프로젝트는 빈 캔버스에서 시작합니다. ||V||를 눌러 샷을 배치하고, 프롬프트를 쓰고, 모델을 선택해 생성합니다. 이것이 기본 워크플로이며, 그다음은 반복입니다.

Martini는 이미지-투-비디오, 비디오-투-비디오, 텍스트-투-비디오 생성을 지원합니다. 샷에 가장 맞는 방식을 선택하세요. 많은 영상 제작자는 생성하거나 업로드한 이미지에서 시작해 그것을 영상의 시작 프레임으로 사용합니다. 이미지는 구도를 고정하고, 모델은 움직임을 만듭니다.`,
  },
  'getting-started/iterating': {
    source_en_hash: 'v1:7e0ld19ccr',
    title: '샷 반복하기',
    description: '빠른 반복으로 샷을 다듬어 갑니다.',
    overview: `첫 생성이 최종 버전이 되는 경우는 거의 없습니다. 프롬프트를 다시 쓰고, 레퍼런스 이미지를 교체하고, 다른 모델을 시도하고, 길이를 조정합니다. 모든 테이크는 원본 옆에 배치되어 나란히 비교할 수 있습니다.

Martini는 생성, 확인, 조정, 재생성이라는 단순한 루프를 중심으로 설계되어 있습니다. 캔버스에는 후보를 펼쳐 둘 여유가 있습니다. 한 샷을 집중해서 다듬을 때는 스택으로 관련 테이크를 한데 모을 수 있습니다.`,
  },
  'canvas/overview': {
    source_en_hash: 'v1:24twlaut3dj',
    title: '개요',
    description: '모든 작업이 놓이는 무한한 워크스페이스입니다.',
    overview: `캔버스는 무한히 펼쳐지는 2D 워크스페이스입니다. 프로젝트의 샷, 이미지, 오디오, 세트, 텍스트 주석이 모두 여기에 놓입니다. 페이지나 폴더 사이를 오갈 필요가 없습니다. 라이트 테이블 위에 셀렉트를 늘어놓거나 벽에 레퍼런스를 붙이듯이 공간적으로 정리합니다.

캔버스 위의 모든 오브젝트는 선택, 이동, 정리에 대해 같은 규칙을 따릅니다. 줌 아웃해서 프로젝트 전체를 보거나, 줌 인해서 프레임을 확인할 수 있습니다. 스크롤로 줌, 두 손가락 드래그로 팬, ||F||로 전체를 화면에 맞춥니다.`,
  },
  'canvas/nodes': {
    source_en_hash: 'v1:1wj9a4wqesi',
    title: '캔버스 오브젝트',
    description: '캔버스에 배치할 수 있는 모든 오브젝트 타입입니다.',
    overview: `단축키를 눌러 배치 모드로 들어간 뒤 캔버스를 클릭해 오브젝트를 배치합니다. 영상 샷은 ||V||, 이미지는 ||I||, 텍스트 주석은 ||T||, 빈은 ||B||, 변수는 ||Shift V||, 세트는 ||Shift S||, 오디오는 ||A||를 사용합니다.

각각 역할이 있습니다. 샷과 이미지는 비주얼 소스를 담습니다. 라이브러리 컬렉션은 프로젝트 전반에서 캐릭터나 소품의 일관성을 유지합니다. 변수는 재사용할 프롬프트 조각을 저장합니다. 빈은 관련 작업을 묶어 둡니다. 주석은 자신이나 협업자에게 남기는 메모입니다. 세트는 샷 생성에 사용할 수 있는 3D 환경입니다.`,
  },
  'canvas/selection': {
    source_en_hash: 'v1:aigbiuhqie',
    title: '선택',
    description: '클릭, Shift+클릭, 마키로 캔버스 위 작업을 선택합니다.',
    overview: `클릭으로 선택합니다. ||Shift||를 누른 채 클릭하면 선택에 추가됩니다. 빈 공간을 드래그하면 마키 선택이 됩니다. ||⌘A||는 전체 선택, ||Escape||는 선택 해제입니다.

선택된 오브젝트에는 테두리가 표시됩니다. 여러 개를 선택하면 한꺼번에 이동, 삭제, 라벨 지정을 할 수 있습니다. 인스펙터에는 마지막으로 선택한 오브젝트의 세부 정보가 표시됩니다.

오브젝트를 드래그하면 내용을 바꾸지 않고 위치를 옮길 수 있습니다. 여러 오브젝트가 선택된 상태에서는 그중 하나를 드래그하면 선택 전체가 상대 위치를 유지한 채 함께 이동합니다.`,
  },
  'canvas/organizing': {
    source_en_hash: 'v1:28jlsrfkc2l',
    title: '정리',
    description: '컬러 라벨, 레이팅, 빈, 필터.',
    overview: `||1||부터 ||5||까지 누르면 컬러 라벨을 적용할 수 있습니다. ||F||는 즐겨찾기, ||R||은 리젝트입니다. 데일리에서 셀렉트를 골라내는 편집자를 위한 정리 도구입니다. 강한 테이크에 표시하고, 약한 테이크를 리젝트하고, 나머지를 정돈합니다.

||B||를 눌러 빈을 만들고, 샷을 끌어다 놓으면 캔버스 위에서 그룹화할 수 있습니다. 빈은 컨테이너이지 폴더가 아닙니다. 모두 보이는 상태로 그대로 접근할 수 있습니다. ||Shift F||로 필터를 열어 즐겨찾기, 리젝트, 특정 컬러 라벨로 좁힐 수 있습니다. 필터는 보기만 바꿉니다.`,
  },
  'canvas/clipboard': {
    source_en_hash: 'v1:19nz99iz0ro',
    title: '복사, 붙여넣기, 복제',
    description: '클립보드 동작과 빠른 복제.',
    overview: `||⌘C||로 복사, ||⌘V||로 붙여넣기, ||⌘D||로 복제합니다. 복사한 오브젝트는 설정을 유지한 채 원본 근처에 붙여넣어집니다. 클립보드를 거치지 않고 빠르게 복사하고 싶을 때는 ||⌘D||가 편리합니다.

이 동작들은 단일 오브젝트와 다중 선택 모두에 사용할 수 있습니다. 다섯 개의 샷을 복사해 캔버스의 다른 위치에 붙여넣으면 상대적인 위치 관계도 그대로 유지됩니다.`,
  },
  'canvas/undo-redo': {
    source_en_hash: 'v1:19hh43keqnm',
    title: '실행 취소와 다시 실행',
    description: '캔버스 동작을 되돌리고 복원합니다.',
    overview: `||⌘Z||는 직전 동작을 되돌립니다. ||⌘ Shift Z||는 다시 실행입니다. 실행 취소는 이동, 삭제, 이름 변경, 오브젝트 생성 등 캔버스 작업에 적용됩니다.

실행 취소는 협업을 고려합니다. 되돌리는 것은 자신의 동작뿐이며 협업자의 동작은 되돌리지 않습니다. 누군가가 작업 중에 노드를 옮겨도 자신의 ||⌘Z||로 그 이동이 되돌려지지는 않습니다.`,
  },
  'canvas/rename': {
    source_en_hash: 'v1:glr66svv1q',
    title: '이름 변경',
    description: '캔버스 위에서 바로 이름을 바꿉니다.',
    overview: `오브젝트를 선택하고 ||Enter||를 누르면 이름을 인라인으로 바꿀 수 있습니다. 새 이름을 입력하고 다시 ||Enter||를 눌러 확정합니다. ||Escape||로 취소할 수 있습니다. 이름은 캔버스 위 오브젝트 아래에 표시되며, 테이크가 많을 때 식별에 도움이 됩니다.`,
  },
  'canvas/delete': {
    source_en_hash: 'v1:8fw602mwuu',
    title: '삭제',
    description: '캔버스에서 작업을 삭제합니다.',
    overview: `||Delete|| 또는 ||Backspace||를 누르면 선택한 오브젝트를 삭제할 수 있습니다. 단일 오브젝트와 다중 선택 모두에 사용할 수 있습니다. 삭제는 되돌릴 수 있으므로 ||⌘Z||로 작업을 복구할 수 있습니다.`,
  },
  'canvas/playback': {
    source_en_hash: 'v1:1xs0lrqf04s',
    title: '재생',
    description: '샷을 미리 보고 영상을 스크럽합니다.',
    overview: `||Space||로 재생과 일시 정지를 전환합니다. ||J||/||K||/||L||은 스크럽입니다. ||J||는 역방향 재생, ||L||은 정방향 재생, ||K||는 정지입니다. ||L||을 두 번 누르면 2배속이 됩니다. 조작은 Premiere, Avid, DaVinci와 동일합니다.

샷을 선택한 뒤 ||Space||를 누르면 캔버스 위에서 바로 프리뷰할 수 있습니다. 타임라인 상단의 프로그램 모니터에는 현재 프레임이 더 크게 표시됩니다.`,
  },
  'generation/overview': {
    source_en_hash: 'v1:65l7fbi17r',
    title: '개요',
    description: 'Martini에서 AI 생성이 작동하는 방식.',
    overview: `Martini는 Veo, Kling, Sora 등 여러 AI 모델에 연결됩니다. 개별 계정이나 API 키는 필요 없습니다. 모델을 고르고, 프롬프트를 쓰고, 파라미터를 설정해 생성합니다. 모든 결과는 캔버스에 놓이므로 반복, 평가, 타임라인 편집을 이어 갈 수 있습니다.

생성은 핵심 루프입니다. 샷을 배치하고, 원하는 내용을 설명하고, 모델을 골라 생성합니다. 결과가 맞지 않으면 조정해 다시 생성합니다. 캔버스에는 테이크를 나란히 펼쳐 두고 비교할 수 있는 공간이 있습니다.`,
  },
  'generation/images': {
    source_en_hash: 'v1:2400a89jp95',
    title: '이미지',
    description: '정지 프레임을 생성해 레퍼런스로 사용합니다.',
    overview: `이미지 생성은 많은 경우 출발점이 됩니다. 정지 프레임을 만들어 그것을 영상 생성의 레퍼런스로 사용합니다. 사진, 스토리보드, 콘셉트 아트를 업로드해 시작 프레임이나 스타일 레퍼런스로 쓸 수도 있습니다.

라이브러리 컬렉션은 특정한 비주얼 아이덴티티에 모델을 고정해 여러 샷에 걸쳐 캐릭터나 소품의 일관성을 유지합니다. 가장 선명한 이미지를 컬렉션에 저장하고 프롬프트에서 @로 참조하면, 그것을 참조하는 모든 샷에서 같은 외형이 유지됩니다.`,
  },
  'generation/video': {
    source_en_hash: 'v1:1smga0lwpzx',
    title: '영상',
    description: '프롬프트와 레퍼런스로 움직이는 샷을 만듭니다.',
    overview: `영상 생성은 프롬프트를 움직이는 샷으로 바꿉니다. 더 세밀하게 제어하고 싶다면 시작 프레임, 끝 프레임, 레퍼런스 이미지를 추가합니다. 모델에 따라 지원하는 제어가 다릅니다. 정확한 카메라 워크를 위해 시작/끝 프레임을 받는 모델도 있고, 텍스트만으로 다루기에 적합한 모델도 있습니다.

길이, 해상도, 화면비는 모델에 따라 다릅니다. Martini는 인터페이스를 통일해 두기 때문에, 의도를 설정하면서 각 모델이 받을 수 있는 파라미터를 확인할 수 있습니다.`,
  },
  'generation/models': {
    source_en_hash: 'v1:1zivmdgst0h',
    title: '모델',
    description: 'Veo, Kling, Sora 등을 어떤 상황에 쓰는지 안내합니다.',
    overview: `모델마다 강한 분야가 있습니다. Veo는 카메라 제어와 시작/끝 프레임 구성에 강하고, Kling은 빠르고 움직임 표현이 뛰어납니다. Sora에는 특유의 영화적 룩이 있습니다. Wan은 스타일라이즈된 콘텐츠에 잘 맞습니다. Ray는 이미지와 영상을 모두 생성합니다.

모델 피커에는 각 모델이 영상 생성, 이미지 생성, 또는 둘 다 중 어디에 해당하는지 표시됩니다. 모든 모델이 모든 파라미터를 받지는 않습니다. 기능이 많은 모델이 아니라 샷에 맞는 모델을 고르세요. 사용하지 않는 모델은 환경 설정에서 숨길 수 있습니다.`,
  },
  'generation/prompting': {
    source_en_hash: 'v1:xyd9da5qjl',
    title: '프롬프트',
    description: '영화적인 결과로 이어지는 프롬프트를 작성합니다.',
    overview: `프롬프트는 설명문이 아니라 연출 지시입니다. 촬영 감독이나 VFX 슈퍼바이저에게 전달하듯이 카메라 움직임, 라이팅, 템포, 샷의 느낌을 적습니다. 추상적인 지시보다 구체적이고 물리적인 표현이 더 잘 동작합니다.

"핸드헬드 팔로우 샷, 창에서 들어오는 자연광, 피사체가 천천히 돌아본다"는 "아름답고 영화적인 인물 샷"보다 훨씬 강한 지시입니다. 카메라 움직임, 광원, 동작을 지정하세요. "stunning"이나 "breathtaking" 같은 형용사는 명확한 모델 지시로 변환되기 어렵기 때문에 피하는 편이 좋습니다.

재생성하기 전에 프롬프트의 의미 있는 요소를 한 번에 하나씩만 바꾸세요. 그래야 무엇이 샷을 개선했는지 판단하기 쉬워집니다.`,
  },
  'generation/upscaling': {
    source_en_hash: 'v1:1th5x70ufdk',
    title: '업스케일',
    description: '해상도를 높이거나 디테일을 향상하거나 HDR로 변환합니다.',
    overview: `업스케일링은 완성된 샷의 해상도를 높여 주며, 디테일을 향상하거나 HDR로 변환할 수도 있습니다. 영상과 이미지 모두에 적용됩니다. 샷이나 이미지를 선택한 뒤 Upscale & Enhance를 고르세요. 우클릭 메뉴, 인스펙터, 또는 ||U|| 키로 실행할 수 있습니다.

![](/docs-imgs/generationUpscale.mp4)

Source: 어느 버전의 샷을 기준으로 작업할지 정하며, 보통은 원본을 사용합니다. 정보 아이콘을 클릭하면 원본의 세부 정보, 즉 크기, 파일 형식, 생성 시점, 에셋 ID를 확인할 수 있습니다.

Preset: 아래의 모든 설정(모델, 해상도, 품질, 인코더 등)을 하나로 묶어 이름을 붙여 저장해 둔 것입니다. Custom은 모든 항목을 직접 설정한다는 뜻이며, 마음에 드는 조합을 찾았다면 더하기 버튼을 눌러 이름을 붙인 프리셋으로 저장해 두고 나중에 재사용할 수 있습니다. 프리셋은 팀과 공유되므로, 프로젝트 전체가 동일한 룩과 사양을 유지할 수 있습니다.

Operation: Upscale은 해상도와 디테일을 높이고, Enhance는 노이즈와 흐림을 정리해 이미지를 복원하며, HDR은 10-bit 또는 16-bit의 하이 다이내믹 레인지 파일을 출력합니다.

Model: 실제 작업을 수행하는 엔진입니다. Proteus 같은 일반 모델은 대부분의 샷에 적합하고, 디테일 및 보정 모델은 선명도나 특정 문제를 겨냥하며, Starlight 계열은 가장 높은 품질을 제공합니다. 이미지 업스케일링에는 전용 고속 모델과 생성형 모델이 따로 마련되어 있습니다.

Resolution: 목표 출력 크기로, HD부터 8K까지 지정할 수 있습니다. Martini는 배율(예: 4x)과 각 코덱이 허용하는 최대 크기를 함께 표시합니다.

Quality: 모델이 이미지를 얼마나 정리하고 선명하게 다듬을지를 정합니다. 기본값은 자동이며, Martini가 적절한 설정을 알아서 골라 줍니다. 룩을 직접 세밀하게 조정하고 싶을 때만 수동 컨트롤(디테일, 노이즈, 흐림, 그레인, 압축)을 추가하면 됩니다.

Output: 인코더는 결과물이 저장되는 형식으로, H.265, H.264, AV1과 함께 HDR 및 ProRes 전달 형식을 지원합니다. 압축은 파일 크기와 품질 사이의 균형을 조절하며, Low는 파일이 더 크지만 디테일을 가장 많이 보존하고, High는 그 반대입니다.

Frame rate: 1초에 몇 개의 프레임이 재생되는지를 나타내며, 값이 높을수록 더 부드럽게 보입니다. Auto는 소스 영상의 프레임 레이트를 그대로 유지하고, 24, 30, 60 fps처럼 원하는 값을 직접 설정할 수도 있습니다.

결과물은 같은 스택에 새 버전으로 추가되므로, 원본과 업스케일된 버전을 전환하며 비교할 수 있습니다.

올리브로 환산한 비용은 모델과 출력 크기에 따라 달라집니다. 영상은 출력 길이 1초 단위로, 이미지는 메가픽셀 단위로 과금되며, Starlight처럼 고품질 모델일수록 비용이 더 높습니다.

![](/docs-imgs/upscale.png)`,
  },
  'generation/credits': {
    source_en_hash: 'v1:101i5vr0a9y',
    title: '올리브',
    description: '생성 비용이 작동하는 방식.',
    overview: `올리브는 Martini의 사용 단위입니다. 모든 생성에는 올리브가 듭니다. 필요한 양은 모델, 해상도, 길이에 따라 달라집니다. 잔액은 계정 메뉴에서 확인할 수 있습니다.

잔액이 떨어지면 추가 구매하거나 플랜을 업그레이드할 수 있습니다. 생성이 실패하면 Martini가 올리브를 자동으로 환불합니다. 생성 전에 계획할 수 있도록 모델 페이지에는 모델별 비용이 표시되어 있습니다.`,
  },
  'sets/overview': {
    source_en_hash: 'v1:cbl38tqd8g',
    title: '개요',
    description: '한 장의 이미지에서 만드는 3D 환경.',
    overview: `세트는 한 장의 이미지에서 구축된 3D 환경입니다. 레퍼런스 사진을 업로드하거나 생성하면 Martini가 그것으로부터 움직일 수 있는 3D 공간을 만듭니다. 세트는 가상의 로케이션이라고 생각하면 됩니다. 그 안에서 카메라를 움직이고, 캐릭터를 배치하고, 특정 각도에서 샷을 생성할 수 있습니다.

이는 실제 로케이션 헌팅이나 물리적 세트 구축에 가장 가까운 기능입니다. 같은 로케이션, 다른 카메라 위치, 그리고 여러 샷에 걸친 일관된 지리 관계를 다룰 수 있습니다.`,
  },
  'sets/creating': {
    source_en_hash: 'v1:2enib07kray',
    title: '세트 만들기',
    description: '이미지를 움직일 수 있는 3D 공간으로 바꿉니다.',
    overview: `생성하거나 업로드한 이미지에서 시작합니다. Martini는 그 이미지에서 움직일 수 있는 3D 환경을 구축합니다. 처리에는 몇 분이 걸립니다.

완료되면 세트는 캔버스에 노드로 표시됩니다. 더블 클릭하면 세트 뷰어가 열려 3D 공간 안을 이동할 수 있습니다.`,
  },
  'sets/characters': {
    source_en_hash: 'v1:23pqljuiw5c',
    title: '캐릭터',
    description: '세트 기반 샷에서 일관된 캐스팅을 유지합니다.',
    overview: `캐릭터는 세트 안에 배치되는 영구적인 아이덴티티입니다. 캐릭터 시트를 업로드하거나 생성하면 Martini가 인물을 감지하고 의상을 추출해 재사용 가능한 레퍼런스를 만듭니다.

세트에서 생성하면 캐릭터가 3D 공간 안에 나타납니다. 덕분에 같은 인물, 같은 의상, 다른 프레이밍이라는 형태로 카메라 앵글에 걸친 캐스팅 일관성을 유지할 수 있습니다.`,
  },
  'sets/viewer': {
    source_en_hash: 'v1:up8l8cp3q2',
    title: '세트 뷰어',
    description: '3D 환경 안을 이동하고 카메라를 배치합니다.',
    overview: `세트 뷰어는 캔버스 안에 임베드된 3D 뷰포트입니다. 드래그로 오빗, 스크롤로 줌, 오른쪽 드래그로 팬을 합니다. 가상 카메라를 환경 안에서 움직이는 셈입니다.

어떤 위치에서든 스크린샷을 레퍼런스 프레임으로 캡처하거나 그 정확한 각도에서 샷을 생성할 수 있습니다. 카메라 위치, 캐릭터, 환경은 모두 생성 프롬프트에 자동으로 반영됩니다.`,
  },
  'timeline/overview': {
    source_en_hash: 'v1:qfy9vuw7gs',
    title: '개요',
    description: '샷을 이어 붙여 시퀀스를 만듭니다.',
    overview: `타임라인은 샷을 시퀀스로 만드는 곳입니다. 캔버스에서 샷을 트랙으로 끌어다 놓고, 순서를 배치하고, 트림하고, 컷을 미리 봅니다. Premiere나 DaVinci를 대체하지는 않지만, 러프 컷을 짜고 타이밍을 확인하고 마감용 패키지를 익스포트하기에는 충분합니다.

툴바의 모드 전환으로 캔버스와 타임라인을 오갑니다. 캔버스는 생성과 정리의 공간이고, 타임라인은 편집의 공간입니다.`,
  },
  'timeline/clips': {
    source_en_hash: 'v1:ha0n4cjv11',
    title: '샷과 트랙',
    description: '샷을 타임라인에 추가하고 트랙 위에 배치합니다.',
    overview: `캔버스에서 샷을 타임라인으로 드래그하면 편집에 추가됩니다. 타임라인 위 샷은 서로, 그리고 재생 헤드에 스냅됩니다. 끝을 드래그해 트림합니다. 트랙은 필요한 만큼 사용할 수 있습니다.

다른 트랙에 샷을 겹쳐 두면 레이어로 다룰 수 있습니다. 타임라인의 샷을 오른쪽 클릭하면 삭제, 슬립, 교체 등의 옵션이 나타납니다.`,
  },
  'timeline/audio': {
    source_en_hash: 'v1:1hlcrir9yjf',
    title: '오디오',
    description: '웨이브폼, 내레이션, 음악.',
    overview: `오디오는 전용 트랙에 배치됩니다. 타임라인에 직접 또는 캔버스에서 파일을 드래그해 오디오를 가져올 수 있습니다. 웨이브폼은 인라인으로 표시되므로 컷을 비트나 대사에 맞출 수 있습니다.

내레이션과 음악 트랙도 같은 방식으로 다룹니다. 배치하고 위치를 조정합니다. Veo 3처럼 오디오 생성을 지원하는 모델은 내장 오디오가 포함된 샷을 만들고, 그 오디오는 타임라인까지 이어집니다.`,
  },
  'timeline/playback': {
    source_en_hash: 'v1:pq7zgl6nne',
    title: '재생',
    description: '트랜스포트 조작으로 편집을 미리 봅니다.',
    overview: `||Space||로 재생과 일시 정지를 전환합니다. ||J||/||K||/||L||로 역방향 스크럽, 정지, 정방향 스크럽을 합니다. 재생 헤드는 프로그램 모니터에 현재 프레임을 표시합니다.

Premiere, Avid, DaVinci와 동일한 트랜스포트 조작입니다. 익숙한 손맛 그대로 사용할 수 있습니다.`,
  },
  'timeline/sequences': {
    source_en_hash: 'v1:mlutts77ri',
    title: '시퀀스',
    description: '편집을 신 단위로 정리합니다.',
    overview: `시퀀스를 사용하면 타임라인을 신 단위로 나눌 수 있습니다. 각 시퀀스는 독립적인 편집이며, 전체를 조립하기 전에 개별 신에 집중하고 싶을 때 유용합니다.

시퀀스를 전환하면 맥락을 잃지 않고 프로젝트의 다른 부분으로 집중을 옮길 수 있습니다. 기존 오프라인 편집에서의 릴이나 막에 해당한다고 생각하면 됩니다.`,
  },
  'export/overview': {
    source_en_hash: 'v1:107qvtix405',
    title: '개요',
    description: 'Martini에서 작업을 익스포트합니다.',
    overview: `익스포트는 Martini의 작업을 외부로 내보내는 단계입니다. 단독 영상으로 MP4를 렌더하거나, Premiere, DaVinci Resolve, CapCut에서 마감하기 위한 XML 패키지를 익스포트할 수 있습니다. XML 패키지에는 미디어 파일과 타임라인 구조가 들어 있어, 편집 소프트웨어에서 열어 작업을 이어 갈 수 있습니다.

어떤 형식을 쓸지는 프로젝트의 다음 행선지에 따라 다릅니다. 리뷰나 납품에는 MP4, 포스트프로덕션에는 XML을 사용합니다.`,
  },
  'export/mp4': {
    source_en_hash: 'v1:17a7qgub68u',
    title: 'MP4',
    description: '영상을 바로 익스포트합니다.',
    overview: `단독 영상 파일이 필요할 때는 MP4로 익스포트합니다. 해상도와 품질을 선택하고 렌더합니다. 출력에는 현재 트림 포인트를 반영한 모든 타임라인 샷이 순서대로 포함됩니다.

러프 컷, 클라이언트 리뷰, SNS 등 영상 자체가 최종 납품물이 되는 경우에 잘 맞습니다.`,
  },
  'export/nle': {
    source_en_hash: 'v1:1wma3l4cdj8',
    title: 'XML 익스포트',
    description: 'Premiere, DaVinci, CapCut용으로 익스포트합니다.',
    overview: `Premiere Pro, DaVinci Resolve, CapCut에서 마감하고 싶다면 XML 패키지를 익스포트합니다. 패키지에는 Martini 타임라인의 미디어, 트랙, 타이밍이 그대로 이어집니다.

컬러 그레이딩, 사운드 디자인, VFX 마감이 필요한 프로젝트에서는 XML을 사용합니다. XML에는 편집 결정 리스트가, 미디어 폴더에는 샷이 들어 있습니다.`,
  },
  'teams/overview': {
    source_en_hash: 'v1:1nwl3a18oii',
    title: '개요',
    description: '조직을 위한 공유 워크스페이스입니다.',
    overview: `팀은 스튜디오나 에이전시를 위한 공유 워크스페이스입니다. 멤버는 결제, 모델 접근 설정, 프로젝트 소유권을 공유합니다. 팀 관리자는 누가 참여할 수 있는지, 어떤 모델을 사용할 수 있는지, 각 멤버가 얼마나 사용할 수 있는지를 관리합니다.

팀은 계정 설정에서 만듭니다. 이메일로 멤버를 초대합니다. 팀 안에서 만든 프로젝트는 모든 멤버에게 보이며, 팀의 올리브 잔액에서 차감됩니다.`,
  },
  'teams/members': {
    source_en_hash: 'v1:10exf22pba9',
    title: '멤버와 역할',
    description: '초대와 권한.',
    overview: `멤버는 이메일로 초대합니다. 관리자는 멤버 추가와 삭제, 팀 설정 조정, 사용량 관리를 담당합니다. 멤버는 팀 프로젝트 안에서 작업하며 팀 결제에서 사용량이 차감됩니다.

누군가가 팀을 떠나도 그 사람의 프로젝트는 남습니다. 잃어버리는 것은 없습니다.`,
  },
  'teams/billing': {
    source_en_hash: 'v1:vbh19d66ga',
    title: '팀 결제',
    description: '공유 결제와 사용량 상한.',
    overview: `팀은 하나의 결제 계정을 공유합니다. 플랜은 팀 전체에 적용되며, 모든 멤버가 같은 올리브 잔액을 사용합니다. 관리자는 멤버별 사용량 상한을 설정해 비용을 관리할 수 있습니다.

결제 내역과 인보이스에서는 멤버별, 프로젝트별 사용량을 확인할 수 있습니다. 올리브가 어디에 사용되었는지 정확히 파악할 수 있습니다.`,
  },
  'teams/models': {
    source_en_hash: 'v1:4buzhbqg6r',
    title: '모델 관리',
    description: '팀에서 사용할 모델을 활성화하거나 비활성화합니다.',
    overview: `팀 관리자는 특정 AI 모델을 활성화하거나 비활성화할 수 있습니다. 어떤 모델이 비싸거나 용도에 비해 불안정하다면 끌 수 있습니다. 비활성화한 모델은 팀 멤버의 모델 피커에 표시되지 않습니다.

이렇게 하면 인터페이스를 좁히고 비용을 예측하기 쉬워집니다. 모델 목록은 팀 설정에서 언제든 변경할 수 있습니다.`,
  },
  'account/profile': {
    source_en_hash: 'v1:19bd8xdwqco',
    title: '프로필',
    description: '별칭과 이메일 주소.',
    overview: `별칭은 Martini 전체에서 나를 식별합니다. 프로젝트의 커서, 협업, 계정 메뉴에 사용됩니다. 이메일 주소는 로그인, 인보이스, 팀 초대에 사용됩니다.

프로필은 계정 설정 페이지에서 갱신할 수 있습니다.`,
  },
  'account/preferences': {
    source_en_hash: 'v1:19yltxzw1cu',
    title: '환경 설정',
    description: '숨긴 모델과 워크플로 설정.',
    overview: `사용하지 않는 모델을 생성 패널에서 숨길 수 있습니다. 환경 설정은 계정별로 저장되며 프로젝트와 팀에 걸쳐 이어집니다.

Veo와 Kling만 사용한다면 그 외 모델은 숨겨 두세요. 생성 패널이 깔끔해지고, 쓰지 않는 모델을 스크롤로 지나칠 필요가 없어집니다.`,
  },
  'account/billing': {
    source_en_hash: 'v1:s6ohjzhuxy',
    title: '결제',
    description: '플랜, 올리브, 거래 내역, 인보이스.',
    overview: `현재 플랜, 올리브 잔액, 거래 내역은 결제 페이지에서 확인할 수 있습니다. 업그레이드, 다운그레이드, 추가 구매를 언제든 할 수 있습니다. 인보이스도 다운로드할 수 있습니다.

사용량 분석에서는 프로젝트별 비용을 확인할 수 있어 올리브가 어디에 쓰였는지 알 수 있습니다. 팀에 소속되어 있다면 팀 결제는 팀 관리자가 별도로 관리합니다.`,
  },
  mcp: {
    source_en_hash: 'v1:1hqksqxtlfe',
    title: 'MCP 설정',
    description: 'MCP를 지원하는 에이전트를 Martini 프로젝트에 연결합니다.',
    overview: `Martini에는 내장 MCP 서버가 있어 에이전트가 프로젝트 안에서 작업할 수 있습니다. Claude, Cursor 또는 자체 도구를 연결해 프로젝트 상태 읽기, 캔버스 오브젝트 생성, 생성 트리거, 캔버스 관리를 수행할 수 있습니다.

한 번 설정해 두면 MCP를 지원하는 어떤 에이전트에서도 제작 작업을 자동화할 수 있습니다.`,
  },
}
